import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Euro, 
  TrendingUp, 
  Calendar, 
  Download, 
  RefreshCw, 
  ChevronLeft, 
  ChevronRight,
  Building2,
  Receipt,
  Percent,
  AlertCircle,
  CheckCircle,
  Settings,
  Save
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TaxBillingPanel = ({ getHeaders }) => {
  const [activeSection, setActiveSection] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [taxConfig, setTaxConfig] = useState(null);
  const [taxReport, setTaxReport] = useState(null);
  const [quarterlyReport, setQuarterlyReport] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [saving, setSaving] = useState(false);
  const [configForm, setConfigForm] = useState({
    tax_rate: 21,
    tax_name: 'IVA',
    company_name: 'Lokalgrow SL',
    company_cif: '',
    company_address: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeSection === 'quarterly') {
      fetchQuarterlyReport();
    }
  }, [selectedYear, selectedQuarter, activeSection]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, reportRes, purchasesRes, invoicesRes] = await Promise.all([
        axios.get(`${API_URL}/api/monetization/tax/config`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/monetization/tax/report`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/monetization/admin/purchases?limit=50`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/monetization/admin/invoices?limit=50`, { headers: getHeaders() })
      ]);

      setTaxConfig(configRes.data);
      setConfigForm({
        tax_rate: configRes.data.tax_rate || 21,
        tax_name: configRes.data.tax_name || 'IVA',
        company_name: configRes.data.company_name || 'Lokalgrow SL',
        company_cif: configRes.data.company_cif || '',
        company_address: configRes.data.company_address || ''
      });
      setTaxReport(reportRes.data);
      setPurchases(purchasesRes.data.purchases || []);
      setInvoices(invoicesRes.data.invoices || []);
    } catch (error) {
      console.error('Error fetching tax data:', error);
      toast.error('Error cargando datos fiscales');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuarterlyReport = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/monetization/tax/quarterly/${selectedYear}/${selectedQuarter}`,
        { headers: getHeaders() }
      );
      setQuarterlyReport(res.data);
    } catch (error) {
      console.error('Error fetching quarterly report:', error);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/monetization/tax/config`, configForm, { headers: getHeaders() });
      toast.success('Configuración fiscal guardada');
      fetchData();
    } catch (error) {
      toast.error('Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-500/20 text-green-400',
      active: 'bg-green-500/20 text-green-400',
      pending: 'bg-yellow-500/20 text-yellow-400',
      cancelled: 'bg-red-500/20 text-red-400'
    };
    return (
      <span className={`px-2 py-0.5 text-xs rounded ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>
        {status === 'completed' ? 'Completado' : 
         status === 'active' ? 'Activo' :
         status === 'pending' ? 'Pendiente' :
         status === 'cancelled' ? 'Cancelado' : status}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const colors = {
      credits: 'bg-blue-500/20 text-blue-400',
      boost: 'bg-purple-500/20 text-purple-400',
      templates: 'bg-pink-500/20 text-pink-400',
      analytics: 'bg-cyan-500/20 text-cyan-400',
      subscription: 'bg-amber-500/20 text-amber-400'
    };
    const labels = {
      credits: 'Créditos',
      boost: 'Destacado',
      templates: 'Plantillas',
      analytics: 'Analíticas',
      subscription: 'Suscripción'
    };
    return (
      <span className={`px-2 py-0.5 text-xs rounded ${colors[type] || 'bg-gray-500/20 text-gray-400'}`}>
        {labels[type] || type}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Impuestos y Facturación</h2>
          <p className="text-gray-400 text-sm mt-1">Gestión fiscal y reporte de IVA</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" /> Actualizar
        </button>
      </div>

      {/* Section Tabs */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="tax-section-tabs">
        {[
          { id: 'overview', label: 'Resumen', icon: TrendingUp },
          { id: 'purchases', label: 'Compras', icon: Receipt },
          { id: 'invoices', label: 'Facturas', icon: FileText },
          { id: 'quarterly', label: 'Trimestrales', icon: Calendar },
          { id: 'config', label: 'Configuración', icon: Settings }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id)}
            data-testid={`tax-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeSection === tab.id
                ? 'bg-emerald-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Section */}
      {activeSection === 'overview' && taxReport && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          {/* Tax Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <Euro className="w-5 h-5 text-emerald-400" />
                </div>
                <span className="text-gray-400 text-sm">Ingresos Netos</span>
              </div>
              <p className="text-2xl font-bold text-white">{formatCurrency(taxReport.totals?.net_revenue)}</p>
              <p className="text-gray-500 text-xs mt-1">Base imponible</p>
            </div>

            <div className="bg-gray-800 rounded-xl p-5 border border-amber-500/30">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <Percent className="w-5 h-5 text-amber-400" />
                </div>
                <span className="text-gray-400 text-sm">{taxConfig?.tax_name || 'IVA'} Recaudado</span>
              </div>
              <p className="text-2xl font-bold text-amber-400">{formatCurrency(taxReport.totals?.tax_collected)}</p>
              <p className="text-gray-500 text-xs mt-1">A pagar al gobierno</p>
            </div>

            <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                </div>
                <span className="text-gray-400 text-sm">Ingresos Brutos</span>
              </div>
              <p className="text-2xl font-bold text-white">{formatCurrency(taxReport.totals?.gross_revenue)}</p>
              <p className="text-gray-500 text-xs mt-1">Total facturado</p>
            </div>

            <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Receipt className="w-5 h-5 text-purple-400" />
                </div>
                <span className="text-gray-400 text-sm">Facturas</span>
              </div>
              <p className="text-2xl font-bold text-white">{taxReport.totals?.invoices || 0}</p>
              <p className="text-gray-500 text-xs mt-1">Este período</p>
            </div>
          </div>

          {/* Tax Breakdown by Product */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-400" />
              Desglose por Producto
            </h3>
            
            {Object.keys(taxReport.by_product_type || {}).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(taxReport.by_product_type).map(([type, data]) => (
                  <div key={type} className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {getTypeBadge(type)}
                      <span className="text-gray-300">{data.count} compras</span>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-semibold">{formatCurrency(data.gross_total)}</p>
                      <p className="text-amber-400 text-sm">{taxConfig?.tax_name}: {formatCurrency(data.tax_total)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No hay ventas en este período</p>
              </div>
            )}
          </div>

          {/* Tax Owed Alert */}
          {taxReport.tax_owed_to_government > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h4 className="text-amber-400 font-semibold mb-1">IVA Pendiente de Declarar</h4>
                  <p className="text-gray-300 text-sm mb-2">
                    Tienes {formatCurrency(taxReport.tax_owed_to_government)} de IVA recaudado que debe ser declarado a Hacienda.
                  </p>
                  <p className="text-gray-400 text-xs">
                    Período: {formatDate(taxReport.period?.start)} - {formatDate(taxReport.period?.end)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Purchases Section */}
      {activeSection === 'purchases' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <div className="p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold">Historial de Compras</h3>
              <p className="text-gray-400 text-sm">Todas las compras de monetización</p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-gray-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Fecha</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Negocio</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Estado</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Importe</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {purchases.length > 0 ? purchases.map((purchase, i) => (
                    <tr key={purchase.id || i} className="hover:bg-gray-700/30">
                      <td className="px-4 py-3 text-sm text-gray-300">{formatDate(purchase.created_at)}</td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm text-white font-medium">{purchase.business_name || 'N/A'}</p>
                          <p className="text-xs text-gray-400">{purchase.business_email || ''}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">{getTypeBadge(purchase.type)}</td>
                      <td className="px-4 py-3">{getStatusBadge(purchase.status)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-white font-semibold">{formatCurrency(purchase.amount)}</span>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                        No hay compras registradas
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}

      {/* Invoices Section */}
      {activeSection === 'invoices' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <div className="p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold">Facturas Emitidas</h3>
              <p className="text-gray-400 text-sm">Todas las facturas con desglose de IVA</p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-gray-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Nº Factura</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Fecha</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Cliente</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Producto</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Base</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">{taxConfig?.tax_name || 'IVA'}</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {invoices.length > 0 ? invoices.map((invoice, i) => (
                    <tr key={invoice.id || i} className="hover:bg-gray-700/30">
                      <td className="px-4 py-3">
                        <span className="text-emerald-400 font-mono text-sm">{invoice.invoice_number}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">{formatDate(invoice.created_at)}</td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm text-white">{invoice.business_info?.name || 'N/A'}</p>
                          <p className="text-xs text-gray-400">{invoice.business_info?.email || ''}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">{getTypeBadge(invoice.purchase_type)}</td>
                      <td className="px-4 py-3 text-right text-gray-300">{formatCurrency(invoice.subtotal)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-amber-400">{formatCurrency(invoice.tax_amount)}</span>
                        <span className="text-gray-500 text-xs ml-1">({invoice.tax_rate}%)</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-white font-semibold">{formatCurrency(invoice.total)}</span>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                        No hay facturas emitidas
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}

      {/* Quarterly Section */}
      {activeSection === 'quarterly' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          {/* Period Selector */}
          <div className="flex items-center gap-4 bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelectedYear(y => y - 1)}
                className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-4 h-4 text-gray-300" />
              </button>
              <span className="text-white font-semibold text-lg px-2">{selectedYear}</span>
              <button
                onClick={() => setSelectedYear(y => y + 1)}
                disabled={selectedYear >= new Date().getFullYear()}
                className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4 text-gray-300" />
              </button>
            </div>

            <div className="flex gap-2">
              {[1, 2, 3, 4].map(q => (
                <button
                  key={q}
                  onClick={() => setSelectedQuarter(q)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    selectedQuarter === q
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                  }`}
                >
                  T{q}
                </button>
              ))}
            </div>
          </div>

          {/* Quarterly Report */}
          {quarterlyReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                  <p className="text-gray-400 text-sm mb-1">Base Imponible</p>
                  <p className="text-2xl font-bold text-white">{formatCurrency(quarterlyReport.totals?.net_revenue)}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-5 border border-amber-500/30">
                  <p className="text-gray-400 text-sm mb-1">IVA Recaudado</p>
                  <p className="text-2xl font-bold text-amber-400">{formatCurrency(quarterlyReport.totals?.tax_collected)}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                  <p className="text-gray-400 text-sm mb-1">Facturas Emitidas</p>
                  <p className="text-2xl font-bold text-white">{quarterlyReport.totals?.invoices || 0}</p>
                </div>
              </div>

              {/* Deadline Alert */}
              {quarterlyReport.declaration_deadline && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-blue-400" />
                  <div>
                    <p className="text-blue-400 font-medium">Fecha límite de declaración</p>
                    <p className="text-gray-300 text-sm">{quarterlyReport.declaration_deadline} (Modelo 303)</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* Config Section */}
      {activeSection === 'config' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
              <Settings className="w-5 h-5 text-emerald-400" />
              Configuración Fiscal
            </h3>

            <div className="space-y-6 max-w-2xl">
              {/* Tax Rate */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Tipo de IVA (%)</label>
                <div className="flex gap-4">
                  <input
                    type="number"
                    value={configForm.tax_rate}
                    onChange={(e) => setConfigForm({ ...configForm, tax_rate: parseFloat(e.target.value) })}
                    className="w-32 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    min="0"
                    max="100"
                    step="0.5"
                  />
                  <input
                    type="text"
                    value={configForm.tax_name}
                    onChange={(e) => setConfigForm({ ...configForm, tax_name: e.target.value })}
                    className="w-32 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="IVA"
                  />
                </div>
                <p className="text-gray-500 text-xs mt-1">Tipo impositivo aplicable en España: 21% general</p>
              </div>

              {/* Company Info */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Nombre de Empresa</label>
                <input
                  type="text"
                  value={configForm.company_name}
                  onChange={(e) => setConfigForm({ ...configForm, company_name: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Lokalgrow SL"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">CIF</label>
                <input
                  type="text"
                  value={configForm.company_cif}
                  onChange={(e) => setConfigForm({ ...configForm, company_cif: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="B12345678"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Dirección Fiscal</label>
                <textarea
                  value={configForm.company_address}
                  onChange={(e) => setConfigForm({ ...configForm, company_address: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                  rows={3}
                  placeholder="Calle Example 123, 28001 Madrid, Spain"
                />
              </div>

              <button
                onClick={handleSaveConfig}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Guardando...' : 'Guardar Configuración'}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default TaxBillingPanel;
